import { api } from '@/shared/services/api';
import type { PaymentTerm } from '@/shared/utils/payment-terms';
import type { NirPrefs } from '@/shared/utils/nir-prefs';

/**
 * Company settings JSON blob — chei cunoscute tipate strict, restul accesibile
 * ca `unknown` (forward-compatible). Adaugă chei noi aici când extindem
 * funcționalitatea (ex: tva, ai, integrations).
 */
export interface CompanySettings {
  /** SPV = sursă de adevăr pt achizițiile interne în D300/D394 (opt-in). */
  d300SpvSource?: boolean;
  d394SpvSource?: boolean;
  /** Înregistrarea automată a cheltuielilor din e-Facturile primite (spec 2026-09-22); `Since` = ISO, setat la activare. */
  spvAutoRegister?: boolean;
  spvAutoRegisterSince?: string;
  paymentTerms?: PaymentTerm[];
  nir?: NirPrefs;
  /** Tipul de document deschis implicit pe /facturi/nou cand utilizatorul nu
   *  specifica ?type= in URL. Setabil per-tenant prin checkbox in dropdown-ul
   *  header invoice-document-header. Default: 'factura'. */
  defaultDocumentType?: 'factura' | 'proforma' | 'aviz' | 'oferta';
  [key: string]: unknown;
}

export interface CompanyData {
  /** Firma are legătură cu Trezoreria Statului (IBAN TREZ / certificat SPV / extrase) — gating sidebar. */
  hasTreasuryPresence?: boolean;
  /** Firma folosește transportul alternativ (config sau conexiune Uber/Bolt/Glovo) — gating sidebar. */
  hasRideHailingPresence?: boolean;
  /** Firma vinde la ghișeu (sesiune/vânzare/bon POS sau casă fiscală) — gating sidebar. */
  hasPosPresence?: boolean;
  id: string;
  name: string;
  cui: string | null;
  jNumber: string | null;
  address: { street?: string; city?: string; county?: string; postalCode?: string } | null;
  logoUrl: string | null;
  stampUrl: string | null;
  phone: string | null;
  email: string | null;
  iban: string | null;
  bankName: string | null;
  settings: CompanySettings;
  caenCode: string | null;
  primaryActivityType: 'services' | 'goods' | 'mixt' | null;
  secondaryCaenCodes: string[];
  legalForm: string | null;
  capitalSocial: number | null;
  website: string | null;
  workPoint: { name?: string; street?: string; city?: string; county?: string; postalCode?: string } | null;
  warehouseName: string | null;
  issuerName: string | null;
  issuerCnp: string | null;
  vatRegime: string;
  taxPeriod: string;
  accountingType: string;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCompanyData {
  name?: string;
  jNumber?: string;
  address?: Record<string, string>;
  logoUrl?: string;
  phone?: string;
  email?: string;
  iban?: string;
  bankName?: string;
  caenCode?: string;
  legalForm?: string;
  capitalSocial?: number;
  website?: string;
  workPoint?: { name?: string; street?: string; city?: string; county?: string; postalCode?: string };
  primaryActivityType?: 'services' | 'goods' | 'mixt' | null;
  secondaryCaenCodes?: string[];
  warehouseName?: string;
  issuerName?: string;
  issuerCnp?: string;
  vatRegime?: string;
  taxPeriod?: string;
  accountingType?: string;
  efacturaEnvironment?: string;
  settings?: Record<string, any>;
}

export interface InvoiceSeries {
  id: string;
  prefix: string;
  type: string;
  nextNumber: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  warehouseId?: string | null;
}

export interface CreateSeriesData {
  nextNumber?: number;
  prefix: string;
  type: string;
  isDefault?: boolean;
  warehouseId?: string | null;
}

export interface BankAccount {
  id: string;
  iban: string;
  bankName: string;
  bankCode: string | null;
  currency: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface CreateBankAccountData {
  iban: string;
  bankName: string;
  currency?: string;
  isDefault?: boolean;
}

export const settingsApi = {
  // Company (formerly Tenant — URL-ul rămâne /tenants/current în Etapa 2; rename URL în Etapa 3)
  getCompany: () => api.get<CompanyData>('/companies/current'),
  updateCompany: (data: UpdateCompanyData) => api.patch<CompanyData>('/companies/current', data),
  /** @deprecated Use getCompany */
  getTenant: () => api.get<CompanyData>('/companies/current'),
  /** @deprecated Use updateCompany */
  updateTenant: (data: UpdateCompanyData) => api.patch<CompanyData>('/companies/current', data),

  // Invoice Series
  getSeries: () => api.get<InvoiceSeries[]>('/invoice-series'),
  createSeries: (data: CreateSeriesData) => api.post<InvoiceSeries>('/invoice-series', data),
  updateSeries: (id: string, data: Partial<CreateSeriesData & { isActive?: boolean }>) =>
    api.patch<InvoiceSeries>('/invoice-series/' + id, data),
  deleteSeries: (id: string) => api.delete('/invoice-series/' + id),

  // Bank Accounts
  getBankAccounts: () => api.get<BankAccount[]>('/bank-accounts'),
  createBankAccount: (data: CreateBankAccountData) => api.post<BankAccount>('/bank-accounts', data),
  updateBankAccount: (id: string, data: Partial<CreateBankAccountData & { isActive?: boolean }>) =>
    api.patch<BankAccount>('/bank-accounts/' + id, data),
  deleteBankAccount: (id: string) => api.delete('/bank-accounts/' + id),
  setDefaultBankAccount: (id: string) => api.patch<BankAccount>('/bank-accounts/' + id + '/default', {}),

  // PDF Preview
  getPreviewPdfUrl: (params: Record<string, string>) => {
    const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';
    const qs = new URLSearchParams(params).toString();
    return `${API_URL}/api/v1/invoices/preview-pdf?${qs}`;
  },
};

// ── Backward-compat aliases (deprecated — vor fi șterse după Etapa 3) ──
/** @deprecated Use CompanyData */
export type TenantData = CompanyData;
/** @deprecated Use UpdateCompanyData */
export type UpdateTenantData = UpdateCompanyData;
